import { ContentSpinner } from '@taxbit-private/cosmic'
import { Suspense, lazy } from 'react'
import { Navigate } from 'react-router-dom'

import { AccountingTab } from 'pages/accounting/helpers/types'

import { BasePath, SettingsPath } from './paths'

const AccountingCosmicContent = lazy(() => import('pages/accounting/AccountingCosmic'))
const ContactsContent = lazy(() => import('pages/contacts/Contacts'))
const DashboardContent = lazy(() => import('pages/dashboard/Dashboard'))
const PrincipalMarketAnalysisPage = lazy(
  () => import('pages/principal-market-analysis/PrincipalMarketAnalysis')
)
const ReportDetails = lazy(() => import('pages/reports/ReportDetails'))
const ReportsFlattened = lazy(() => import('pages/reports/ReportsFlattened'))
const RulesContent = lazy(() => import('pages/rules/Rules'))
const SettingsContent = lazy(() => import('pages/settings/Settings'))
const SourcesContent = lazy(() => import('pages/sources/Sources'))
const EntitiesContent = lazy(() => import('pages/super-admin/entities/Entities'))
const TransactionsContent = lazy(() => import('pages/transactions/Transactions'))
const VoucherDetails = lazy(() => import('pages/vouchers/VoucherDetails'))
const Vouchers = lazy(() => import('pages/vouchers/Vouchers'))

export type Route = {
  path: string
  name: string
  component: JSX.Element
  isDisabled?: boolean
}

export const DASHBOARD_ROUTE: Route = {
  path: BasePath.Dashboard,
  name: 'Dashboard',
  component: (
    <Suspense fallback={<ContentSpinner />}>
      <DashboardContent />
    </Suspense>
  ),
}

const TRANSACTIONS_ROUTE: Route = {
  path: BasePath.Transactions,
  name: 'Transactions',
  component: (
    <Suspense fallback={<ContentSpinner />}>
      <TransactionsContent />
    </Suspense>
  ),
}

const REPORT_DETAILS_ROUTE: Route = {
  path: BasePath.ReportDetails,
  name: 'ReportDetails',
  component: (
    <Suspense fallback={<ContentSpinner />}>
      <ReportDetails />
    </Suspense>
  ),
}

const REPORTS_ROUTE: Route = {
  path: BasePath.Reports,
  name: 'Reports',
  component: (
    <Suspense fallback={<ContentSpinner />}>
      <ReportsFlattened />
    </Suspense>
  ),
}

export const SOURCES_ROUTE: Route = {
  path: BasePath.Sources,
  name: 'Sources',
  component: (
    <Suspense fallback={<ContentSpinner />}>
      <SourcesContent />
    </Suspense>
  ),
}

export const CONTACTS_ROUTE: Route = {
  path: BasePath.Contacts,
  name: 'Contacts',
  component: (
    <Suspense fallback={<ContentSpinner />}>
      <ContactsContent />
    </Suspense>
  ),
}

export const CONTACTS_SAVED_ROUTE: Route = {
  path: BasePath.ContactsSaved,
  name: 'Saved Contacts',
  component: (
    <Suspense fallback={<ContentSpinner />}>
      <ContactsContent />
    </Suspense>
  ),
}

export const CONTACTS_UNKNOWN_ADDRESSES_ROUTE: Route = {
  path: BasePath.ContactsUnknownAddresses,
  name: 'Unknown Addresses',
  component: (
    <Suspense fallback={<ContentSpinner />}>
      <ContactsContent />
    </Suspense>
  ),
}

export const MAPPINGS_ROUTE: Route = {
  path: BasePath.AccountingMapping,
  name: 'Detailed Mappings',
  component: (
    <Navigate replace={true} to={`/app/${BasePath.Accounting}/${AccountingTab.DetailedMappings}`} />
  ),
}

export const ACCOUNTING_ROUTE: Route = {
  path: BasePath.Accounting,
  name: 'Accounting',
  component: (
    <Suspense fallback={<ContentSpinner />}>
      <AccountingCosmicContent />
    </Suspense>
  ),
}

export const RULES_ROUTE: Route = {
  path: BasePath.Rules,
  name: 'Rules',
  component: (
    <Suspense fallback={<ContentSpinner />}>
      <RulesContent />
    </Suspense>
  ),
}

const SETTINGS_ROUTE: Route = {
  path: BasePath.Settings,
  name: 'Settings',
  component: (
    <Suspense fallback={<ContentSpinner />}>
      <SettingsContent />
    </Suspense>
  ),
} as const

const ACCOUNTING_SETTINGS_ROUTE: Route = {
  path: SettingsPath.Accounting,
  name: 'Accounting Settings',
  component: (
    <Suspense fallback={<ContentSpinner />}>
      <SettingsContent />
    </Suspense>
  ),
}

const TEAM_SETTINGS_ROUTE: Route = {
  path: SettingsPath.Team,
  name: 'Team Settings',
  component: (
    <Suspense fallback={<ContentSpinner />}>
      <SettingsContent />
    </Suspense>
  ),
}

const SPAM_TOKENS_SETTINGS_ROUTE: Route = {
  path: SettingsPath.SpamTokens,
  name: 'Spam Tokens Settings',
  component: (
    <Suspense fallback={<ContentSpinner />}>
      <SettingsContent />
    </Suspense>
  ),
}

const AUDIT_LOG_SETTINGS_ROUTE: Route = {
  path: SettingsPath.AuditLog,
  name: 'Audit Log',
  component: (
    <Suspense fallback={<ContentSpinner />}>
      <SettingsContent />
    </Suspense>
  ),
}

const STABLE_COINS_SETTINGS_ROUTE: Route = {
  path: SettingsPath.StableCoins,
  name: 'Stable Coins Settings',
  component: (
    <Suspense fallback={<ContentSpinner />}>
      <SettingsContent />
    </Suspense>
  ),
}

const CATEGORIES_SETTINGS_ROUTE: Route = {
  path: SettingsPath.Categories,
  name: 'Categories Settings',
  component: (
    <Suspense fallback={<ContentSpinner />}>
      <SettingsContent />
    </Suspense>
  ),
}

const CUSTOM_FIELDS_SETTINGS_ROUTE: Route = {
  path: SettingsPath.CustomFields,
  name: 'Custom Fields Settings',
  component: (
    <Suspense fallback={<ContentSpinner />}>
      <SettingsContent />
    </Suspense>
  ),
}

export const PMA_ROUTE: Route = {
  path: BasePath.PrincipalMarketAnalysis,
  name: 'Principal Market Analysis',
  component: (
    <Suspense fallback={<ContentSpinner />}>
      <PrincipalMarketAnalysisPage />
    </Suspense>
  ),
}

export const CASES_ROUTE: Route = {
  path: BasePath.Cases,
  name: 'Cases',
  component: (
    <Suspense fallback={<ContentSpinner />}>
      <EntitiesContent />
    </Suspense>
  ),
}

export const VOUCHERS_ROUTE: Route = {
  path: BasePath.Vouchers,
  name: 'Vouchers',
  component: (
    <Suspense fallback={<ContentSpinner />}>
      <Vouchers />
    </Suspense>
  ),
}

export const VOUCHER_DETAILS_ROUTE: Route = {
  path: BasePath.VoucherDetails,
  name: 'Voucher Details',
  component: (
    <Suspense fallback={<ContentSpinner />}>
      <VoucherDetails />
    </Suspense>
  ),
}

export const VOUCHER_DETAILS_OVERVIEW_ROUTE: Route = {
  path: BasePath.VoucherDetailsOverview,
  name: 'Voucher Details Overview',
  component: (
    <Suspense fallback={<ContentSpinner />}>
      <VoucherDetails />
    </Suspense>
  ),
}

export const VOUCHER_DETAILS_TRANSACTIONS_ROUTE: Route = {
  path: BasePath.VoucherDetailsTransactions,
  name: 'Voucher Details Transactions',
  component: (
    <Suspense fallback={<ContentSpinner />}>
      <VoucherDetails />
    </Suspense>
  ),
}

export const ALL_ROUTES: Route[] = [
  DASHBOARD_ROUTE,
  TRANSACTIONS_ROUTE,
  REPORT_DETAILS_ROUTE,
  REPORTS_ROUTE,
  SOURCES_ROUTE,
  CONTACTS_ROUTE,
  CONTACTS_SAVED_ROUTE,
  CONTACTS_UNKNOWN_ADDRESSES_ROUTE,
  MAPPINGS_ROUTE,
  ACCOUNTING_ROUTE,
  RULES_ROUTE,
  SETTINGS_ROUTE,
  ACCOUNTING_SETTINGS_ROUTE,
  TEAM_SETTINGS_ROUTE,
  SPAM_TOKENS_SETTINGS_ROUTE,
  STABLE_COINS_SETTINGS_ROUTE,
  CATEGORIES_SETTINGS_ROUTE,
  AUDIT_LOG_SETTINGS_ROUTE,
  CUSTOM_FIELDS_SETTINGS_ROUTE,
  PMA_ROUTE,
  CASES_ROUTE,
  VOUCHERS_ROUTE,
  VOUCHER_DETAILS_ROUTE,
]

// Grand canyon default routes
export const GRAND_CANYON_ROUTES: Route[] = [
  DASHBOARD_ROUTE,
  TRANSACTIONS_ROUTE,
  REPORT_DETAILS_ROUTE,
  REPORTS_ROUTE,
  SOURCES_ROUTE,
  SETTINGS_ROUTE,
  ACCOUNTING_SETTINGS_ROUTE,
  TEAM_SETTINGS_ROUTE,
  SPAM_TOKENS_SETTINGS_ROUTE,
  STABLE_COINS_SETTINGS_ROUTE,
  CATEGORIES_SETTINGS_ROUTE,
  AUDIT_LOG_SETTINGS_ROUTE,
  CASES_ROUTE,
]

/**
 * Any route that is eligible to appear in the top-level navbar.
 */
export const NAVBAR_ROUTES = [
  DASHBOARD_ROUTE,
  TRANSACTIONS_ROUTE,
  REPORT_DETAILS_ROUTE,
  REPORTS_ROUTE,
  SOURCES_ROUTE,
  CONTACTS_ROUTE,
  RULES_ROUTE,
  ACCOUNTING_ROUTE,
  RULES_ROUTE,
  PMA_ROUTE,
  VOUCHERS_ROUTE,
]
